/**
 * Voice AI — Guest Memory Page (Orchestrator)
 * Thin shell: grid ↔ detail view, toast, data loading.
 * Dispatches: (none — top-level page component)
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';
import { AbortableLoader } from '../../services/abortable-loader.js';

import './guest-grid.js';
import './guest-detail.js';

export class GuestPage extends LitElement {
  static properties = {
    guests: { type: Array },
    selectedPhone: { type: String },
    _loading: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _toastVisible: { type: Boolean, state: true },
    _toastMessage: { type: String, state: true },
    _toastType: { type: String, state: true },
    _total: { type: Number, state: true },
    _page: { type: Number, state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    guestStyles,
  ];

  constructor() {
    super();
    this.guests = [];
    this.selectedPhone = null;
    this._loading = true;
    this._searchQuery = '';
    this._toastVisible = false;
    this._toastMessage = '';
    this._toastType = 'success';
    this._total = 0;
    this._page = 1;
    this._loader = new AbortableLoader();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadGuests();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._loader.abort();
  }

  /* ─── Data Loading ─── */

  async _loadGuests() {
    this._loading = true;

    const result = await this._loader.run(signal =>
      guestService.listGuests({
        search: this._searchQuery,
        page: this._page,
        limit: 50,
      }, { signal })
    );

    if (result.aborted) return;

    if (result.error) {
      console.warn('[Guests] API unavailable:', result.error.message);
      this.guests = [];
    } else if (result.data.success) {
      this.guests = result.data.guests || [];
      this._total = result.data.total || 0;
    } else {
      this.guests = [];
      this._total = 0;
    }

    this._loading = false;
  }

  /* ─── Event Handlers ─── */

  _onSelectGuest(e) {
    this.selectedPhone = e.detail.phone;
  }

  _onBack() {
    this.selectedPhone = null;
    this._loadGuests(); // Refresh in case profile was edited
  }

  _onSearch(e) {
    this._searchQuery = e.detail.query;
    this._page = 1;
    this._loadGuests();
  }

  _onPageChange(e) {
    this._page = e.detail.page;
    this._loadGuests();
  }

  _onToast(e) {
    this._showToast(e.detail.message, e.detail.type || 'success');
  }

  _showToast(message, type = 'success') {
    this._toastMessage = message;
    this._toastType = type;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 3000);
  }

  /* ─── Render ─── */

  render() {
    return html`
      ${iconStyles}
      ${this.selectedPhone ? html`
        <guest-detail
          .phone=${this.selectedPhone}
          @back=${this._onBack}
          @toast=${this._onToast}
        ></guest-detail>
      ` : html`
        <guest-grid
          .guests=${this.guests}
          .loading=${this._loading}
          .searchQuery=${this._searchQuery}
          .total=${this._total}
          .page=${this._page}
          @select-guest=${this._onSelectGuest}
          @search=${this._onSearch}
          @page-change=${this._onPageChange}
        ></guest-grid>
      `}
      <div class="toast ${this._toastVisible ? 'visible' : ''} ${this._toastType === 'error' ? 'error' : ''}">
        <i class="fa-sharp fa-light ${this._toastType === 'error' ? 'fa-circle-xmark' : 'fa-check'}"></i>
        ${this._toastMessage || 'Saved successfully'}
      </div>
    `;
  }
}

customElements.define('guest-page', GuestPage);
